/*
 * The primary rail.
 *
 * Ported from Yash Yeole's `ui-sidebar` branch (commit f8bcd76), which was built
 * on `staging`. This is a port rather than a merge: that branch predates the
 * Iteration 2.0 token layer and the shared primitive layer, and it removed a top
 * header that this branch had already removed by other means, so the two
 * histories solve the same problem in different files.
 *
 * His CSS would in fact have rendered at the time: every variable it uses
 * (`--ink-title`, `--soft-bg`, `--pill-bg`, `--muted-2/3`, `--shadow-hover`,
 * bare `--radius`) resolved through the legacy compatibility shim at the foot of
 * `tokens.css`, and the three keyframes it animates all live in `base.css`. The
 * problem was what that would have meant: ~800 lines of *new* CSS leaning on a
 * shim whose own comment said nothing new should reference it. That shim has
 * since been deleted along with `legacy.css`, so the same CSS would render
 * unstyled today — which is the argument, made by events.
 *
 * Kept from his version: expand/collapse with the choice persisted, the
 * off-canvas drawer below the mobile breakpoint, the in-progress project card
 * with its stage label / percent / ETA, and the "Watch now" pill that appears
 * when a generation finishes.
 *
 * Rebuilt on this branch's primitives: his fifteen inline icons (thirteen
 * already existed in `icons.tsx`), his two hand-rolled popovers (`Menu` has the
 * focus trap, roving tabindex and arrow-key handling his did not), his inline
 * cancel confirmation (`ConfirmDialog`), and his `data-tip` CSS tooltips, which
 * assistive tech cannot see.
 *
 * Contract the rest of the app depends on:
 *   - named export `Sidebar`, no required props; `AppLayout` renders it directly
 *   - the rail owns its own width, in `shell.css` on `.rail` and
 *     `.rail.is-collapsed`; no layout outside this file states a number
 *   - it rests collapsed and opens on hover, on focus, or pinned by the toggle.
 *     All three produce the same layout: the gutter widens and the page reflows.
 *     There is no separate peeked *appearance* any more, and so no `is-peek`
 *     class — it styled nothing once hovering stopped floating over the page,
 *     and a class that changes nothing is worse than no class at all
 *   - the inner `<nav>` is named `Primary`; `App.routing.test.tsx` finds the
 *     shell by that name on seven routes
 *   - `useProcessing()` and `useCurrentUser()` are read as is. ProcessingProvider
 *     owns the per-job WebSocket, the 15s REST fallback, the 3-strike error rule,
 *     wallet sync and localStorage mirroring — none of it is re-derived here.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@/app/providers/user';
import { useLogoutRedirect } from '@/features/auth/hooks/useLogoutRedirect';
import { useProcessing } from '@/app/providers/processing';
import { useNotifications } from '@/app/providers/notifications';
import { useWallet } from '@/app/providers/wallet';
import { useShelves } from '@/features/library/hooks/useShelves';
import { useEtaCountdown } from '@/shared/hooks/useEtaCountdown';
import { MEDIA, useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { ConfirmGenerationModal } from '@/features/generation/components/ConfirmGenerationModal';
import { formatCredits, initialsOf } from '@/shared/utils/format';
import { safeErrorMessage } from '@/shared/utils/errors';
import { etaSecondsFor, formatEta, isActiveJob, summarizeProgress } from '@/shared/utils/jobProgress';
import type { ProcessingJob } from '@/shared/types';
import type { Shelf } from '@/features/library/services/shelfService';
import {
  Button,
  ButtonLink,
  ConfirmDialog,
  HomeIcon,
  IconButton,
  LibraryIcon,
  Menu,
  MenuIcon,
  MenuItem,
  MenuSeparator,
  MenuSubmenu,
  MoreIcon,
  PanelToggleIcon,
  Pill,
  PlayIcon,
  AddCircleIcon,
  ProgressBar,
  ShelfIcon,
  Spinner,
  Tooltip,
  TrashIcon,
  ValoiVLockup,
  ValoiV,
  WalletIcon,
  cx,
} from '@/shared/ui';

/* The rail stays how you left it across reloads. Guarded because Safari's
   private mode throws on setItem rather than failing quietly, and a sidebar is
   not worth taking the app down for. Folds into the shared namespaced-storage
   helper when the six services that hand-roll the same thing are unified. */
const COLLAPSE_KEY = 'valoi.sidebar.collapsed';

/* Expanded unless the user has collapsed it. Reversed from the original default,
   which rested the rail as a 56px strip: that suited an app whose users already
   knew the icons, and it is the wrong first impression for anyone who does not --
   a column of unlabelled glyphs with no way to discover what they are short of
   hovering each one.
 
   The test is for the collapsed value rather than the expanded one, so an absent
   key means expanded: `=== '1'` on a missing key would read as collapsed and
   reintroduce exactly the default this replaces. Collapsing still sticks, because
   the toggle writes the key. */
function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    /* Storage blocked (private mode, or a locked-down browser): expanded, matching
       the no-key default above rather than contradicting it. */
    return false;
  }
}

function writeCollapsed(value: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSE_KEY, value ? '1' : '0');
  } catch {
    /* persistence is a nicety, not a requirement */
  }
}

/* `Home`, not `Search`. The page is a landing page — a NOW SHOWING hero and
   three content rails — and the only search on it is a small field in the
   corner, which is what gave the Figma frame its name and then gave this rail
   item a magnifying glass. Production has always called the same content Home,
   so a user arriving from there looked for that word and found a magnifier.
   `ui-inventory.md` carried this as an open question; it is answered now. */
type NavItem = {
  to: string;
  label: string;
  Icon: (props: { size?: number }) => ReactNode;
  end: boolean;
};

const NAV: NavItem[] = [
  { to: '/', label: 'New content', Icon: AddCircleIcon, end: true },
  { to: '/home', label: 'Home', Icon: HomeIcon, end: false },
  { to: '/library', label: 'Library', Icon: LibraryIcon, end: false },
];


/* -------------------------------------------------------------------------- */

export function Sidebar() {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { jobs, cancelJob, confirmJob } = useProcessing();
  const { pushNotification } = useNotifications();

  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  /* No signed-out special case any more: `readCollapsed` defaults everyone to
     expanded, so a first-time visitor gets the labels without overriding a stored
     preference on their behalf. */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [peeking, setPeeking] = useState(false);

  /* Collapsing is a desktop affordance. Below the breakpoint the rail is a
     drawer that is either open or absent, so `collapsed` is ignored rather than
     producing a 56px strip floating over the content.

     Read through `useMediaQuery` rather than a local `matchMedia` effect, and
     the difference shows on the first paint. The effect version had to render
     once before it could know, so it always rendered the desktop rail first and
     corrected on the next frame — a flash of the wrong layout on every mobile
     load. It also carried its own copy of the breakpoint string, which is the
     drift `MEDIA` and the parity guard in `responsive.test.ts` exist to stop:
     the number here and the one in `shell.css` have to be the same number. */
  const isDrawer = useMediaQuery(MEDIA.drawer);

  /* Resting collapsed, expanding on approach, pinned by the toggle — the three
     states a Claude/ChatGPT rail has.

     `peekable` is the resting strip: collapsed, on a desktop width. Peeking off
     that opens the rail for real — the gutter widens and the page reflows, the
     same as pinning it. It used to float the panel over the page instead, which
     meant one control gave two different results depending on whether you
     hovered or clicked.

     `narrow` — icons only, tooltips on — is therefore *not* the same as
     `collapsed`: a peeking rail is collapsed but showing everything. Every
     content decision below keys off `narrow`; only the gutter keys off
     `collapsed`. */
  /* Signed-in only. `RootRoute` mounts this in the authenticated branch alone -- the
     public landing page draws its own top bar -- so the signed-out variant this
     component used to carry (a Login pill in the foot, PUBLIC_NAV, `/login` link
     rewriting) is gone rather than merely unreachable.

     Home is back in NAV, reversing the note that used to sit here. The reasoning was
     that the catalogue is the public page, so a signed-in copy would be the same page
     twice -- true, except `/` is the composer once you are authenticated, so the
     featured videos had no route at all behind the wall. `/home` renders the same
     LandingPage with `signedIn`, so it is one page reachable from both sides rather
     than two. */
  const navItems = NAV;

  /* Rests as a strip and widens on approach; `readCollapsed` decides the resting
     state from storage, defaulting to expanded when nothing is stored. */
  const peekable = !isDrawer && collapsed;
  const narrow = peekable && !peeking;

  useEffect(() => {
    if (!peekable) setPeeking(false);
  }, [peekable]);

  const toggleCollapsed = useCallback(() => {
    /* The write is outside the updater, which is the rule this file's own
       CollapsedJobs.onExpand already follows and ProcessingProvider states at
       length: StrictMode double-invokes updaters and a concurrent render can
       run one for a pass React then throws away. Persisting from in there
       wrote a rail state the UI never adopted — and that state wins on the
       next reload. */
    const next = !collapsed;
    setCollapsed(next);
    writeCollapsed(next);
    /* Pinning open and un-pinning both end the peek. Without this, un-pinning
       leaves the pointer sitting on a rail that is now collapsed, so it
       immediately peeks back open and the toggle looks like it did nothing. */
    setPeeking(false);
  }, [collapsed]);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  /* Newest last, so the list reads top-to-bottom in the order work was started
     and a new job appears at the bottom rather than shunting the others down. */
  const activeJobs = useMemo(
    () => jobs.filter(isActiveJob).sort((a, b) => a.submittedAt - b.submittedAt),
    [jobs],
  );

  /* A job that finishes drops out of the active list immediately. Holding onto
     it lets the card offer "Watch now" before the video slips away into the
     library — otherwise the only signal that a generation succeeded is a card
     silently vanishing. */
  const [readyIds, setReadyIds] = useState<string[]>([]);
  const previousActiveRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const stillActive = new Set(activeJobs.map((job) => job.id));
    const finished: string[] = [];
    previousActiveRef.current.forEach((id) => {
      if (stillActive.has(id)) return;
      if (jobs.find((job) => job.id === id)?.status === 'completed') finished.push(id);
    });
    previousActiveRef.current = stillActive;
    if (finished.length > 0) {
      setReadyIds((prev) => [...prev, ...finished.filter((id) => !prev.includes(id))]);
    }
  }, [jobs, activeJobs]);

  const readyJobs = useMemo(
    () =>
      readyIds
        .map((id) => jobs.find((job) => job.id === id))
        .filter((job): job is ProcessingJob => job?.status === 'completed'),
    [readyIds, jobs],
  );

  const dismissReady = useCallback((id: string) => {
    setReadyIds((prev) => prev.filter((entry) => entry !== id));
  }, []);

  const watchJob = useCallback(
    (job: ProcessingJob) => {
      navigate(`/videos/${job.videoId ?? job.id}`);
      dismissReady(job.id);
      closeDrawer();
    },
    [navigate, dismissReady, closeDrawer],
  );

  /* Shelf membership keys on the library item id, and a generated video's
     library id *is* its job id (see LibraryCard, which calls cancelJob with
     item.id). So the same hook that drives the library rail and the content
     title menu drives this one — one source of truth, four surfaces. */
  const jobIds = useMemo(
    () => [...activeJobs, ...readyJobs].map((job) => job.id),
    [activeJobs, readyJobs],
  );
  const { shelves, membership, addTo, removeFrom } = useShelves(jobIds);

  /* Hoisted out of the card, and rendered as a sibling of the rail rather than
     inside the Menu. React routes synthetic events along the component tree, not
     the DOM tree, so a dialog rendered under <Menu> would bubble its button
     clicks into the menu's own close-on-click handler — and the menu unmounts
     the dialog with it. Same reason ShelfPanel and LibraryCard keep theirs
     outside. */
  const [pendingCancel, setPendingCancel] = useState<ProcessingJob | null>(null);

  /* Same reasoning, same place: the quote dialog is a sibling of the rail. Held
     by id rather than by value so the modal follows the job as the provider
     updates it — a quote that lands a moment after the dialog opens has to
     replace "Calculating…" without the user reopening anything. */
  const [reviewId, setReviewId] = useState<string | null>(null);
  const reviewJob = reviewId ? jobs.find((job) => job.id === reviewId) : undefined;

  const confirmCancel = useCallback(() => {
    const job = pendingCancel;
    setPendingCancel(null);
    if (!job) return;
    void cancelJob(job.id).catch((error: unknown) => {
      /* Said out loud, because otherwise it is not said at all. This used to
         swallow the rejection on the grounds that ProcessingProvider reports
         its own failures — it does not: its only notification is the
         job-is-ready one, and `transitionJob` rethrows. So the dialog closed,
         the generation carried on, and nothing on screen had changed. The page
         this rail replaces printed the reason (usually insufficient credits) on
         the job's own row; the rail has no room for that, and the notification
         centre is where this app already puts asynchronous failures. */
      pushNotification({
        message: safeErrorMessage(error, `Couldn’t cancel “${job.title}”. Please try again.`),
      });
    });
  }, [pendingCancel, cancelJob, pushNotification]);

  const hasJobs = activeJobs.length > 0 || readyJobs.length > 0;

  return (
    <>
      {/* Only rendered as a control below the drawer breakpoint; CSS hides it
          above, where the rail is always on screen. */}
      <button
        type="button"
        className="rail-drawer-toggle"
        aria-label="Open menu"
        aria-expanded={drawerOpen}
        onClick={() => setDrawerOpen(true)}
      >
        <MenuIcon size={20} />
      </button>

      <div
        className={cx('rail-scrim', drawerOpen && 'is-visible')}
        onClick={closeDrawer}
        aria-hidden
      />

      <aside
        className={cx(
          'rail',
          narrow && 'is-collapsed',
          drawerOpen && 'is-open',
        )}
        aria-label="Sidebar"
        onMouseEnter={peekable ? () => setPeeking(true) : undefined}
        onMouseLeave={peekable ? () => setPeeking(false) : undefined}
        /* Focus counts as approach. React's onFocus/onBlur bubble, so tabbing
           into any control inside the strip opens it — otherwise a keyboard
           user would navigate a rail whose labels they cannot see. */
        onFocus={peekable ? () => setPeeking(true) : undefined}
        onBlur={
          peekable
            ? (event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setPeeking(false);
                }
              }
            : undefined
        }
      >
        <div className="rail__panel">
          {/* One toggle, always mounted, always the same 40x40 box in the same
              place. It used to be two: `narrow` rendered a brand-sized button on
              the left, and anything else rendered a *different*, smaller
              IconButton on the right. Approaching the rail flips `narrow`, so the
              button you were aiming at was unmounted and replaced by one 20px
              away and 10px smaller while your click was still travelling — the
              click landed on the logo link behind it instead, which then held the
              rail open on focus. The rail looked like it had stopped responding
              to clicks entirely. A control that moves when you reach for it is
              the bug; keeping it still is the fix. */}
          <div className="rail__head">
            <RailTip show={narrow} content={narrow ? 'Open sidebar' : 'Collapse sidebar'}>
              <button
                type="button"
                className="rail__brand-toggle"
                aria-label={narrow ? 'Open sidebar' : 'Collapse sidebar'}
                aria-expanded={!narrow}
                onClick={toggleCollapsed}
              >
                {/* Collapsed, the brand *is* the toggle: the mark gives way to the
                    panel glyph under the pointer. Open, the mark stays hidden and
                    the glyph is simply the control. */}
                <ValoiV size={26} className="rail__mark" />
                <PanelToggleIcon size={20} className="rail__brand-panel" />
              </button>
            </RailTip>
            {/* Named for where it goes, and `/` is two different places. Signed in
                it is the prompt page, and calling it "home" would collide with the
                rail's own Home item pointing elsewhere -- two adjacent links both
                announcing "home" and then parting ways is worse for a screen reader
                than for anyone else. Signed out `/` IS the landing page, and there
                the rail's Home item points at the same route, so "home" is simply
                what this is. */}
            {narrow ? null : (
              <NavLink
                /* The mark goes to the catalogue, not the composer. A logo is a way
                   back to the front of the product, and for a signed-in user that is
                   Home -- `/` would drop them into an empty prompt box, which is a
                   destination, not a home. */
                to={user ? '/home' : '/'}
                className="rail__brand"
                aria-label="Valoi — home"
                end
                onClick={closeDrawer}
              >
                {/* Inline SVG on `currentColor`, like the mark above — it used
                    to be an <img> with a baked black wordmark, which is why the
                    rail could not tint it green. */}
                {/* `size` signed out, because the component renders width AND
                    height as attributes: a stylesheet width alone leaves the height
                    attribute in place and squashes the lockup. Out here the mark is
                    the only branding on the page; in the app it sits above a full
                    navigation and the smaller default is right. */}
                <ValoiVLockup className="rail__logo" />
              </NavLink>
            )}
          </div>

          <nav className="rail__nav" aria-label="Primary">
            <ul className="rail__nav-list">
              {navItems.map(({ to, label, Icon, end }) => (
                <li key={to}>
                  <RailTip show={narrow} content={label}>
                    {/* Straight to `to`. The rail only renders for a signed-in
                        user now, so there is no signed-out case to rewrite -- an
                        earlier version pointed every destination at `/login` while
                        the rail was on the landing page advertising walled pages. */}
                    <NavLink
                      to={to}
                      end={end}
                      aria-label={label}
                      onClick={closeDrawer}
                      className={({ isActive }) => cx('rail__item', isActive && 'is-active')}
                    >
                      <span className="rail__item-icon">
                        <Icon size={19} />
                      </span>
                      {/* Not rendered rather than hidden with CSS. `display: none`
                          would take it out of the accessible name too, leaving the
                          link named only by `aria-label` — but nothing in the DOM
                          would say so, so a test could not tell the difference and
                          dropping that attribute would go unnoticed. */}
                      {narrow ? null : <span className="rail__item-label">{label}</span>}
                    </NavLink>
                  </RailTip>
                </li>
              ))}
            </ul>
          </nav>

          {hasJobs ? (
            narrow ? (
              <CollapsedJobs
                activeCount={activeJobs.length}
                readyCount={readyJobs.length}
                onExpand={() => {
                  setCollapsed(false);
                  writeCollapsed(false);
                }}
              />
            ) : (
              <div className="rail__jobs">
                {readyJobs.length > 0 ? (
                  <section aria-label="Ready to watch">
                    <h2 className="rail__jobs-title">Ready</h2>
                    {readyJobs.map((job) => (
                      <ReadyCard
                        key={job.id}
                        job={job}
                        onWatch={() => watchJob(job)}
                        onDismiss={() => dismissReady(job.id)}
                      />
                    ))}
                  </section>
                ) : null}

                {activeJobs.length > 0 ? (
                  <section aria-label="In progress">
                    <h2 className="rail__jobs-title">In progress</h2>
                    {activeJobs.map((job) => (
                      <ActiveCard
                        key={job.id}
                        job={job}
                        shelves={shelves}
                        shelfIds={membership[job.id] ?? []}
                        onAddToShelf={(shelfId) => void addTo(job.id, shelfId)}
                        onRemoveFromShelf={(shelfId) => void removeFrom(job.id, shelfId)}
                        onRequestCancel={() => setPendingCancel(job)}
                        onReview={() => setReviewId(job.id)}
                        onNavigate={closeDrawer}
                      />
                    ))}
                  </section>
                ) : null}
              </div>
            )
          ) : null}

          {/* The foot is owned here rather than by UserChip, which used to render
              `.rail__foot` itself. It has two occupants now, and the rule that
              separates them from the job list has to sit above both — inside the
              chip it would have drawn *between* the wallet row and the chip,
              which says they belong to different groups when they are the same
              one. */}
          {/* The rail only ever renders for a signed-in user now -- the landing page
              dropped it for a top bar, because every item in it was a destination a
              visitor cannot reach. The signed-out branch that used to live here (a
              Login pill in place of the wallet row and user chip) went with it. */}
          {user ? (
            <div className="rail__foot">
              <WalletRow collapsed={narrow} onNavigate={closeDrawer} />
              <UserChip collapsed={narrow} onNavigate={closeDrawer} />
            </div>
          ) : null}
        </div>
      </aside>

      <ConfirmDialog
        open={pendingCancel !== null}
        title="Cancel this generation?"
        description="The video won't be created, and this cannot be undone."
        confirmLabel="Cancel generation"
        cancelLabel="Keep generating"
        onConfirm={confirmCancel}
        onCancel={() => setPendingCancel(null)}
      />

      {/* The same dialog the prompt and content pages use, not a second pair of
          inline buttons. Before this the rail could only cancel a quoted job:
          confirming meant finding your way to `/videos/:id`, and a user who
          dismissed the post-submit modal had no way back to the quote at all. */}
      {reviewId ? (
        <ConfirmGenerationModal
          open
          status={reviewJob?.status}
          estimatedCost={reviewJob?.estimatedCost}
          estimatedDurationSeconds={reviewJob?.estimatedDurationSeconds}
          errorDetail={reviewJob?.errorDetail}
          onConfirm={async () => {
            if (reviewJob) await confirmJob(reviewJob.id);
            setReviewId(null);
          }}
          onCancel={async () => {
            if (reviewJob) await cancelJob(reviewJob.id);
            setReviewId(null);
          }}
          onDismiss={() => setReviewId(null)}
        />
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */

/** A tooltip only when the label it duplicates is hidden. */
/* Always a Tooltip, disabled rather than absent when the rail is wide.
   Swapping between `<Tooltip>` and a bare fragment changes the element type, so
   React remounts everything below it — and now that the rail widens on focus,
   that remount lands in the middle of clicking the user chip and throws the
   menu's open state away before it can render. */
function RailTip({ show, content, children }: { show: boolean; content: string; children: ReactNode }) {
  return (
    <Tooltip content={content} placement="right" decorative disabled={!show}>
      {children}
    </Tooltip>
  );
}

/* -------------------------------------------------------------------------- */

function CollapsedJobs({
  activeCount,
  readyCount,
  onExpand,
}: {
  activeCount: number;
  readyCount: number;
  onExpand: () => void;
}) {
  const total = activeCount + readyCount;
  const allReady = activeCount === 0;
  /* One chip for the whole list rather than one per job. Chips carry no title,
     so several of them would be indistinguishable, and "watch" is ambiguous the
     moment more than one video is ready — expanding puts the user in front of
     the actual choice instead of guessing for them. */
  const label = allReady
    ? `${readyCount} ${readyCount === 1 ? 'video' : 'videos'} ready to watch`
    : `${total} ${total === 1 ? 'generation' : 'generations'} in progress`;

  return (
    <div className="rail__jobs rail__jobs--collapsed">
      <Tooltip content={label} placement="right" decorative>
        <button
          type="button"
          className={cx('rail-chip', allReady && 'is-ready')}
          aria-label={label}
          onClick={onExpand}
        >
          {allReady ? <PlayIcon size={16} /> : <Spinner size={16} />}
          <span className="rail-chip__count">{total}</span>
        </button>
      </Tooltip>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function ActiveCard({
  job,
  shelves,
  shelfIds,
  onAddToShelf,
  onRemoveFromShelf,
  onRequestCancel,
  onReview,
  onNavigate,
}: {
  job: ProcessingJob;
  shelves: Shelf[];
  shelfIds: string[];
  onAddToShelf: (shelfId: string) => void;
  onRemoveFromShelf: (shelfId: string) => void;
  onRequestCancel: () => void;
  onReview: () => void;
  onNavigate: () => void;
}) {
  const summary = summarizeProgress(job);
  const remaining = useEtaCountdown(etaSecondsFor(job));
  const percent = Math.round(summary.fraction * 100);
  const onShelf = useMemo(() => new Set(shelfIds), [shelfIds]);
  const movable = shelves.filter((shelf) => !shelf.builtin);
  /* Nothing is rendering and no time is passing, so this card shows neither a
     spinner, a bar nor an ETA — it shows what it is waiting for. */
  const awaitingUser = job.status === 'awaiting_confirmation';
  /* Characters drawn, waiting for the user. The review itself lives on the video's
     page (it needs room for nine images), so this card links there rather than
     opening a dialog. */
  const awaitingReview = job.status === 'awaiting_reference_review';

  return (
    <article className="rail-job">
      <div className="rail-job__top">
        {/* A link, because the content page is where the stage-by-stage detail
            lives — this card only has room for one line of it. Without this the
            checklist was reachable only by knowing the URL. */}
        <h3 className="rail-job__title" title={job.title}>
          <Link to={`/videos/${job.videoId ?? job.id}`} onClick={onNavigate}>
            {job.title}
          </Link>
        </h3>

        <Menu
          label={`${job.title} actions`}
          placement="top-end"
          trigger={
            <IconButton label={`${job.title} actions`} size="sm" variant="ghost">
              <MoreIcon size={16} />
            </IconButton>
          }
        >
          {/* Yash's version had this disabled with a "Soon" badge because
              `staging` has no shelves. This branch does, and it is the same
              hook the library and content pages use. */}
          {movable.length > 0 ? (
            <MenuSubmenu label="Move to shelf" icon={<ShelfIcon size={16} />}>
              {movable.map((shelf) => (
                <MenuItem
                  key={shelf.id}
                  onSelect={() =>
                    onShelf.has(shelf.id) ? onRemoveFromShelf(shelf.id) : onAddToShelf(shelf.id)
                  }
                  checked={onShelf.has(shelf.id)}
                >
                  {shelf.name}
                </MenuItem>
              ))}
            </MenuSubmenu>
          ) : null}

          {/* "Rename project" is deliberately absent rather than present and
              disabled. There is no rename endpoint for a job, and a permanently
              greyed row labelled "Soon" is a promise the UI cannot keep. */}
          {movable.length > 0 ? <MenuSeparator /> : null}
          <MenuItem icon={<TrashIcon size={16} />} tone="danger" onSelect={onRequestCancel}>
            Cancel generation
          </MenuItem>
        </Menu>
      </div>

      {awaitingUser ? (
        <>
          <Pill tone="warn" dot>
            {summary.label}
          </Pill>
          <div className="rail-job__actions">
            <Button variant="primary" size="sm" onClick={onReview}>
              Review &amp; confirm
            </Button>
          </div>
        </>
      ) : awaitingReview ? (
        <>
          <Pill tone="warn" dot>
            {summary.label}
          </Pill>
          <div className="rail-job__actions">
            <ButtonLink
              variant="primary"
              size="sm"
              to={`/videos/${job.videoId ?? job.id}`}
              onClick={onNavigate}
            >
              Review characters
            </ButtonLink>
          </div>
        </>
      ) : (
        <>
          <div className="rail-job__status">
            <Spinner size={13} />
            <span className="rail-job__stage">{summary.label}</span>
            {summary.indeterminate ? null : <span className="rail-job__pct">{percent}%</span>}
          </div>

          <ProgressBar
            size="sm"
            value={summary.indeterminate ? undefined : summary.fraction}
            label={`${job.title} progress`}
          />

          {remaining !== undefined ? <p className="rail-job__eta">{formatEta(remaining)}</p> : null}
        </>
      )}
    </article>
  );
}

/* -------------------------------------------------------------------------- */

function ReadyCard({
  job,
  onWatch,
  onDismiss,
}: {
  job: ProcessingJob;
  onWatch: () => void;
  onDismiss: () => void;
}) {
  return (
    <article className="rail-job rail-job--ready">
      <h3 className="rail-job__title" title={job.title}>
        {job.title}
      </h3>
      <p className="rail-job__stage">Your video is ready</p>
      <div className="rail-job__actions">
        <Button variant="primary" size="sm" iconLeft={<PlayIcon size={13} />} onClick={onWatch}>
          Watch now
        </Button>
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    </article>
  );
}

/* -------------------------------------------------------------------------- */

/*
 * Wallet, one click from anywhere.
 *
 * It was reachable only through the account menu — open the chip, then choose
 * Wallet — which is two clicks and a hidden one, for the page a user goes to
 * precisely when something has stopped working for want of credits.
 *
 * It sits in the foot rather than joining the three items at the top: those are
 * where content lives (make one, find one, revisit one) and money is not a
 * content destination. Putting it here also means the balance is on screen
 * whenever the rail is open, next to the only other number in the shell.
 *
 * Rendered as a `rail__item` so hover, the active tint and the collapsed 40x40
 * box all come from the nav's own rules rather than a second set that would
 * drift from them.
 */
function WalletRow({ collapsed, onNavigate }: { collapsed: boolean; onNavigate: () => void }) {
  const { balance } = useWallet();
  const credits = balance ? formatCredits(balance.balanceCredits) : null;

  /* Same reasoning as the chip below: the balance belongs in the accessible
     name, not only in the drawing. Collapsed, this label is also the tooltip,
     which is the only way the number is available at all. */
  const label = credits ? `Wallet, ${credits} credits` : 'Wallet';

  return (
    <RailTip show={collapsed} content={label}>
      <NavLink
        to="/wallet"
        aria-label={label}
        onClick={onNavigate}
        className={({ isActive }) => cx('rail__item', 'rail__wallet', isActive && 'is-active')}
      >
        <span className="rail__item-icon">
          <WalletIcon size={19} />
        </span>
        {collapsed ? null : (
          <>
            <span className="rail__item-label">Wallet</span>
            {/* aria-hidden because `label` above already says it, and reading
                "Wallet, 42.50 credits" and then "42.50" is the same number
                twice. */}
            {credits ? (
              <span className="rail__wallet-balance" aria-hidden>
                {credits}
              </span>
            ) : null}
          </>
        )}
      </NavLink>
    </RailTip>
  );
}

/* -------------------------------------------------------------------------- */

function UserChip({ collapsed, onNavigate }: { collapsed: boolean; onNavigate: () => void }) {
  const { user } = useCurrentUser();
  const logoutAndGoHome = useLogoutRedirect();
  const navigate = useNavigate();

  if (!user) return null;

  const initials = initialsOf(user.displayName);

  /* No balance here, drawn or announced. The chip used to carry it on the
     grounds that it was the one always-on-screen place for the number — but
     `WalletRow` sits directly above it and does exactly that, so the foot was
     printing the same figure twice, once under a wallet icon and once under the
     user's name. Screen readers had it worse than the drawing did: two adjacent
     controls whose names both ended in the same "42.50 credits". The wallet row
     owns the balance; this owns the identity. */
  const chipName = user.displayName;

  const go = (path: string) => {
    navigate(path);
    onNavigate();
  };

  return (
    <RailTip show={collapsed} content={chipName}>
      <Menu
        label="Account"
        placement="top-start"
        trigger={
          /* A bespoke composite control rather than `Button` or `IconButton`:
             it is an avatar plus two lines of text that collapses to just the
             avatar, which is neither of those primitives' shape. */
          <button type="button" className="rail-user" aria-label={chipName}>
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="rail-user__avatar" />
            ) : (
              <span className="rail-user__avatar rail-user__avatar--initials" aria-hidden>
                {initials}
              </span>
            )}
            {collapsed ? null : (
              <span className="rail-user__meta" aria-hidden>
                <span className="rail-user__name">{user.displayName}</span>
              </span>
            )}
          </button>
        }
      >
        {/* No icons on these, deliberately: we have no person or sign-out
            glyph, and icons on some rows but not others reads as an oversight.
            Their accessible names are also pinned by App.routing.test.tsx.

            No Wallet row. It was here first, then the rail grew its own row and
            this stayed as "a second way in costs nothing" — but the two sit a
            few pixels apart in the same foot, so the cost was a menu that
            repeats the control immediately above it. `WalletRow` is the one
            route to /wallet from the shell. */}
        <MenuItem onSelect={() => go('/account')}>Account</MenuItem>
        {/* Admin sat in the old top nav, immediately before Settings — the
            redesign deleted that nav, so it lands here in the same position
            rather than being lost with it. Hiding the row from non-admins is a
            tidiness measure, not a control: `RequireAdmin` bounces the route and
            `require_admin_user` guards every call behind it. */}
        {user.role === 'admin' ? (
          <MenuItem onSelect={() => go('/admin')}>Admin</MenuItem>
        ) : null}
        <MenuItem onSelect={() => go('/settings')}>Settings</MenuItem>
        <MenuSeparator />
        <MenuItem
          onSelect={() => {
            /* Home, not wherever RequireUser would bounce them. Clearing the user
               alone left the guard to redirect, and it sends signed-out callers to
               /login -- right for someone who typed a protected URL, wrong for
               someone who just chose to leave: they get a sign-in form for the
               account they were signing out of. The landing page is the front door
               and it is public, so it is where leaving should land. */
            /* The ordering that makes that work -- catch, then finally, then
               navigate -- lives in the hook, which is also where it is explained. */
            logoutAndGoHome();
          }}
        >
          Log out
        </MenuItem>
      </Menu>
    </RailTip>
  );
}
